import { Dialog } from '@madpaws/design-system';
import { Formik } from 'formik';
import { useRouter } from 'next/router';
import React from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { trackEvent } from '~/components/analytics/analytics';
import { USER_SIGNUP_CLICK_EVENT_NAME } from '~/components/analytics/constants';
import { DOM_CONTAINER_ID } from '~/components/constants';
import { AuthApi } from '~/services/auth/authApi';
import { getJWTData } from '~/utils/auth';

import { EmailSignUpForm } from './EmailSignUpForm';
import { emailSignUpSchema } from './utils';
import {
  AuthMethods,
  CHECKBOX_SPECIAL_OFFERS_NAME,
  CHECKBOX_TERMS_NAME,
  RecaptchaActions,
} from '../constants';
import { useAuthenticationContext } from '../useAuthenticationContext';

import type { OnSignUpSuccessHandler } from '../types';
import type { ReactElement } from 'react';

type Props = {
  email: string;
  onSignUpSuccess: OnSignUpSuccessHandler;
};

export type EmailSignUpFormValues = {
  checkboxOptions: string[];
  comment: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
};

export const EmailSignUpDialog = ({ onSignUpSuccess, email }: Props): ReactElement => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const formInitialValues = {
    email,
    password: '',
    checkboxOptions: [CHECKBOX_SPECIAL_OFFERS_NAME],
    firstName: '',
    lastName: '',
    comment: '',
  };

  const {
    query: { refSource, referralCode },
  } = useRouter();

  const [apiError, setApiError] = React.useState<string | null>(null);

  const { setIsEmailSignUpDialogOpen, isEmailSignUpDialogOpen } = useAuthenticationContext();

  const handleEmailSignUpDialogClose = (): void => {
    setIsEmailSignUpDialogOpen(false);
  };

  const onSubmitForm = async (values: EmailSignUpFormValues): Promise<void> => {
    setApiError(null);

    trackEvent(USER_SIGNUP_CLICK_EVENT_NAME, {
      email: values.email,
      grant_type: AuthMethods.PASSWORD,
      receive_special_offers: values.checkboxOptions.includes(CHECKBOX_SPECIAL_OFFERS_NAME),
    });

    const recaptchaTokenV3 = await executeRecaptcha?.(RecaptchaActions.SIGN_UP);

    try {
      const response = await AuthApi.createUser({
        client_id: Number(process.env.NEXT_PUBLIC_CLIENT_ID ?? 0),
        client_secret: process.env.NEXT_PUBLIC_CLIENT_SECRET ?? '',
        code: '',
        email: values.email,
        firstName: values.firstName,
        grant_type: AuthMethods.PASSWORD,
        lastName: values.lastName,
        password: values.password,
        passwordConfirmation: values.password,
        qantasSrc: false,
        receiveSpecialOffers: values.checkboxOptions.includes(CHECKBOX_SPECIAL_OFFERS_NAME),
        refSource: refSource as string | '',
        referralCode: referralCode as string | '',
        terms: values.checkboxOptions.includes(CHECKBOX_TERMS_NAME),
        comment: values.comment,
        recaptchaTokenV3: recaptchaTokenV3,
      });

      const jwtData = getJWTData(response.access_token as string);
      const userId = jwtData?.sub as number;

      onSignUpSuccess(response, { email: values.email, userId }, AuthMethods.PASSWORD, {
        receiveSpecialOffers: values.checkboxOptions.includes(CHECKBOX_SPECIAL_OFFERS_NAME),
      });

      handleEmailSignUpDialogClose();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setApiError(error[0]?.detail);
    }
  };

  return (
    <Dialog
      domContainerId={DOM_CONTAINER_ID}
      heading="Welcome to Mad Paws"
      isOpen={isEmailSignUpDialogOpen}
      onRequestClose={handleEmailSignUpDialogClose}
    >
      <Formik
        initialValues={formInitialValues}
        onSubmit={onSubmitForm}
        validationSchema={emailSignUpSchema}
      >
        <EmailSignUpForm apiError={apiError} />
      </Formik>
    </Dialog>
  );
};
